import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Trash, Crown, ShieldCheck, User } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLES = ["owner", "admin", "staff"];
const roleIcon = { owner: Crown, admin: ShieldCheck, staff: User };
const roleTone = { owner: "bg-amber-50 text-amber-700 border-amber-200", admin: "bg-emerald-50 text-emerald-700 border-emerald-200", staff: "bg-zinc-100 text-zinc-700 border-zinc-200" };

export default function UsersManagement() {
  const { user: current } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "staff" });
  const isOwner = current?.role === "owner";

  const load = () => api.get("/admin/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      await api.post("/admin/users", { email: form.email.trim().toLowerCase(), role: form.role });
      toast.success("User added");
      setOpen(false); setForm({ email: "", role: "staff" }); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };

  const changeRole = async (email, role) => {
    try {
      await api.put(`/admin/users/${encodeURIComponent(email)}`, { role });
      toast.success("Role updated"); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email}?`)) return;
    try {
      await api.delete(`/admin/users/${encodeURIComponent(email)}`);
      toast.success("Removed"); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>Team & Roles</h1>
          <p className="text-sm text-zinc-500 mt-1">Invite Gmail accounts and assign roles. Users sign in with Google.</p>
        </div>
        {isOwner && (
          <Button onClick={() => setOpen(true)} data-testid="add-user-btn">
            <UserPlus size={14} className="mr-2" />Invite User
          </Button>
        )}
      </div>

      <Card className="border-zinc-200">
        <CardContent className="p-0">
          <Table data-testid="admin-users-table">
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Added By</TableHead>
                <TableHead>Since</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((u) => {
                const Ic = roleIcon[u.role] || User;
                const isSelf = current?.email === u.email;
                return (
                  <TableRow key={u.email} data-testid={`admin-user-row-${u.email}`}>
                    <TableCell className="font-medium">{u.email}{isSelf && <span className="ml-2 text-[10px] uppercase text-zinc-500">(you)</span>}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${roleTone[u.role]} gap-1`}>
                        <Ic size={12} /> {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-zinc-500">{u.added_by || "—"}</TableCell>
                    <TableCell className="text-xs text-zinc-500 font-mono">{u.created_at?.slice(0, 10)}</TableCell>
                    <TableCell className="text-right">
                      {isOwner && !isSelf && (
                        <div className="flex items-center gap-2 justify-end">
                          <Select value={u.role} onValueChange={(v) => changeRole(u.email, v)}>
                            <SelectTrigger className="h-8 w-28 text-xs" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Button size="icon" variant="ghost" onClick={() => remove(u.email)} data-testid={`remove-user-${u.email}`}>
                            <Trash size={14} />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!items.length && <TableRow><TableCell colSpan={5} className="text-center py-10 text-zinc-500">No admin users yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="text-xs text-zinc-500 space-y-1 max-w-2xl">
        <p><strong>owner</strong> — full access + can manage this list.</p>
        <p><strong>admin</strong> — full operational access (orders, events, menu, products).</p>
        <p><strong>staff</strong> — same operational access; reserved for future granular limits.</p>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invite Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Google email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="name@gmail.com" data-testid="new-user-email" />
              <p className="text-xs text-zinc-500 mt-1">User must sign in with this Gmail address.</p>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={add} disabled={!form.email} data-testid="save-new-user">Invite</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
